// ============================================================
// THE PLATE'S WORDS BESIDE THE FIGURE (plan design-gaps, P4): the last hit at
// the frame's top-left, kept after the hit has played, and the sights at the
// right, on the row after it, once a called shot's place is locked. The rows
// are the layout's (engine/plate/calloutLayout), which keeps them clear. The lines are the engine's
// (engine/plate/readout); this only sets them in the plate's type.
// ============================================================

import { useState } from 'react';
import type { Impact } from '../../engine/impacts';
import { WORDS_STEP as STEP, WORDS_TOP as TOP, sightsRow, type Frame } from '../../engine/plate/calloutLayout';
import { lastHitLines, sightsLine, type ReadoutLooks } from '../../engine/plate/readout';
import styles from '../../styles/BodyPlate.module.css';

/** The inset from the frame's edge, in frame units (the lines' baseline and step are the layout's, which keeps them clear). */
const INSET = 60;

interface Props {
  last: Impact | null;
  looks: ReadoutLooks;
  /** A location's display name (the figure's own), by engine id. */
  nameOf: (id: string) => string;
  frame: Frame;
  /** The sights' locked place's name; null with none locked (or no sights up), which says nothing. */
  sights: string | null;
  /** The hover readout has the top line: the last hit steps aside while it shows (the Track P review). */
  quiet: boolean;
}

/** The last hit's lines at the top-left, the sights' at the top-right. */
export default function PlateWords({ last, looks, nameOf, frame, sights, quiet }: Props) {
  const left = frame.x + INSET;
  const right = frame.x + frame.w - INSET;
  const lines = last ? lastHitLines(last, looks, nameOf) : [];
  return (
    <g pointerEvents="none">
      {last && !quiet && (
        <text className={styles.words} x={left} y={TOP} data-testid="plate-last-hit">
          {lines.map((line, i) => (
            <tspan key={i} x={left} dy={i ? STEP : 0}>
              {line}
            </tspan>
          ))}
        </text>
      )}
      {sights && (
        // Its own row after the last hit's and the readout's, so neither ever covers it; only once a place is locked.
        <text
          className={styles.words}
          x={right}
          y={TOP + sightsRow(lines.length) * STEP}
          textAnchor="end"
          data-testid="plate-sights"
        >
          {sightsLine(sights)}
        </text>
      )}
    </g>
  );
}

/** The latest impact this plate has seen, kept after the live one lets it go (the last hit stays in words). */
export function useLastImpact(impact: Impact | null | undefined): Impact | null {
  const [last, setLast] = useState<Impact | null>(impact ?? null);
  if (impact && impact !== last) setLast(impact);
  return impact ?? last;
}
